import {ApolloError} from 'apollo-client';
import gql from 'graphql-tag';
import {useMemo} from 'react';

import {useWorkspaceTemplatesQuery} from '../../generated/graphql';
import {LinePlotSettings} from '../WorkspaceDrawer/Settings/types';

/**
 * This fetches the workspace templates the user created within the entity.
 * If the entity is an org that is NOT on the enterprise plan, then workspaceTemplates
 * will return an empty list.
 */
export const WORKSPACE_TEMPLATES_QUERY = gql`
  query WorkspaceTemplates($entityId: ID!, $userId: ID) {
    workspaceTemplates(entityId: $entityId, userId: $userId) {
      id
      createdAt
      createdBy {
        id
        name
        email
        username
      }
      updatedAt
      linePlotSettings {
        smoothingType
        smoothingWeight
        useRunsTableGroupingInPanels
        xAxis
        xLogScale
        xAxisMin
        xAxisMax
        maxRuns
        groupAgg
        xAxisFormat
        yLogScale
        yAxisMin
        yAxisMax
      }
    }
  }
`;

/**
 * We are iteratively adding which fields should be supported in templates.
 * This allows us to remember the areas that we need to handle for the feature.
 */
export type LinePlotTemplateSettings = Pick<
  LinePlotSettings,
  | 'groupAgg'
  | 'maxRuns'
  | 'smoothingType'
  | 'smoothingWeight'
  | 'useRunsTableGroupingInPanels'
  | 'xAxis'
  | 'xLogScale'
  | 'xAxisMin'
  | 'xAxisMax'
  | 'xAxisFormat'
  | 'yLogScale'
  | 'yAxisMin'
  | 'yAxisMax'
>;

// This type guarantees that fields exist, but value may be undefined
// This helps with typing when the query result hook is used
export type WorkspaceTemplate = {
  id: string;
  createdAt: string;
  createdBy: {
    id: string;
    name: string;
    email: string | undefined;
    username: string | undefined;
  };
  updatedAt: string | undefined;
  linePlotSettings: LinePlotTemplateSettings;
};

/**
 * This hook is used to fetch the workspace templates the user created in the selected account.
 *
 * @param entityId - this is the entityOrgId from the selected account.
 * The entities db table support different types - regular entity like team and org.
 * This allows us to simplify api and workspace_templates db cols.
 */
export const useWorkspaceTemplatesQueryResult = ({
  entityId,
  userId,
  skip,
}: {
  entityId: string | undefined;
  userId: string | undefined;
  skip?: boolean;
}): {
  workspaceTemplates: WorkspaceTemplate[];
  isLoadingWorkspaceTemplates: boolean;
  workspaceTemplatesError: ApolloError | undefined;
  refetchWorkspaceTemplates: () => void;
} => {
  const {data, loading, error, refetch} = useWorkspaceTemplatesQuery({
    variables: {entityId: entityId ?? '', userId},
    skip: entityId == null || userId == null || skip,
  });

  return useMemo(() => {
    if (loading || error != null) {
      return {
        workspaceTemplates: [],
        isLoadingWorkspaceTemplates: loading,
        workspaceTemplatesError: error,
        refetchWorkspaceTemplates: refetch,
      };
    }

    const workspaceTemplates: WorkspaceTemplate[] =
      data?.workspaceTemplates.map(template => {
        // Explicitly maps fields to undefined if they are null to
        // avoid a bunch of "?? undefined" everywhere

        const linePlotSettings: LinePlotTemplateSettings = {
          groupAgg: template.linePlotSettings?.groupAgg ?? undefined,
          maxRuns: template.linePlotSettings?.maxRuns ?? undefined,
          smoothingType: template.linePlotSettings?.smoothingType ?? undefined,
          smoothingWeight:
            template.linePlotSettings?.smoothingWeight ?? undefined,
          useRunsTableGroupingInPanels:
            template.linePlotSettings?.useRunsTableGroupingInPanels ??
            undefined,
          xAxis: template.linePlotSettings?.xAxis ?? undefined,
          xLogScale: template.linePlotSettings?.xLogScale ?? undefined,
          xAxisMin: template.linePlotSettings?.xAxisMin ?? undefined,
          xAxisMax: template.linePlotSettings?.xAxisMax ?? undefined,
          xAxisFormat: template.linePlotSettings?.xAxisFormat ?? undefined,
          yLogScale: template.linePlotSettings?.yLogScale ?? undefined,
          yAxisMin: template.linePlotSettings?.yAxisMin ?? undefined,
          yAxisMax: template.linePlotSettings?.yAxisMax ?? undefined,
        };

        return {
          id: template.id,
          createdAt: template.createdAt,
          createdBy: {
            id: template.createdBy.id,
            name: template.createdBy.name,
            email: template.createdBy.email ?? undefined,
            username: template.createdBy.username ?? undefined,
          },
          updatedAt: template.updatedAt ?? undefined,
          linePlotSettings,
        };
      }) ?? [];
    return {
      workspaceTemplates,
      isLoadingWorkspaceTemplates: loading,
      workspaceTemplatesError: error,
      refetchWorkspaceTemplates: refetch,
    };
  }, [data, loading, error, refetch]);
};

export const UPSERT_WORKSPACE_TEMPLATE_QUERY = gql`
  mutation UpsertWorkspaceTemplate(
    $entityId: ID!
    $id: ID
    $linePlotSettings: LinePlotSettingsInput
  ) {
    upsertWorkspaceTemplate(
      input: {entityId: $entityId, id: $id, linePlotSettings: $linePlotSettings}
    ) {
      id
    }
  }
`;

export const DELETE_WORKSPACE_TEMPLATE_QUERY = gql`
  mutation DeleteWorkspaceTemplate($id: ID!) {
    deleteWorkspaceTemplate(input: {id: $id}) {
      success
    }
  }
`;
